import { QueryClient } from '@tanstack/react-query'

import { setLoginCookie } from '@hooks/useAuthentication/utils'
import { OPEN_CONTENT_ACCESS_STATUS } from '@hooks/useGetContentAccessStatus'
import { GnsUserState } from '@hooks/useGnsStateUser'
import { setCustomVariables } from '@components/Piano/utils'

export interface InitializeGnsParams {
  /**
   * Passed to the external `GaaMetering.init()` function to represent
   * the current user state.
   */
  userState: GnsUserState

  /**
   * The Auth0 redirect URL required for third-party login via OneLog.
   */
  authorizationUrl: string
  queryClient: QueryClient
}

const handleRegisterUserPromise = async (userState: GnsUserState) => {
  return Promise.resolve(userState)
}

const handleLoginPromise = async (
  authorizationUrl: string,
  userState: GnsUserState
): Promise<GnsUserState> => {
  await window?.GaaMetering?.getLoginPromise?.()

  if (authorizationUrl) {
    window?.location?.assign?.(authorizationUrl)
  }

  return Promise.resolve(userState)
}

const handleShowPaywall = (userState: GnsUserState) => {
  // Do nothing because our PianoRestrictedDrawer is already active
  return userState
}

const handleUnlockArticle = (queryClient: QueryClient) => {
  // Update the Piano experience with isGnsArticleUnlocked set to true
  setCustomVariables({ isGnsArticleUnlocked: 'true' })
  window?.tp?.experience?.execute?.()

  // Remove the PianoRestrictedDrawer
  queryClient.setQueryData(
    ['content-access-status'],
    OPEN_CONTENT_ACCESS_STATUS
  )
  queryClient.invalidateQueries({
    queryKey: ['content-access-status'],
    exact: true,
  })
}
const publisherEntitlementPromise = async (
  userState: GnsUserState
): Promise<GnsUserState> => {
  return userState
}

const handleSimulateShowcase = () => {
  // comment in if you want to see the debug logs from Google with the #swg.debug=1 hash
  const url = `${window?.location?.href}#swg.debug=1`
  const enrichedUrl = `https://play.google.com/newsstand/api/v3/articleaccess?testurl=${url}`
  const encodedURL = encodeURIComponent(enrichedUrl)
  const redirectURL = `https://www.google.com/url?sa=j&url=${encodedURL}`
  window.location.href = redirectURL
}

const hasGAAParam = () => {
  const url =
    (window?.location?.href && new URL(window?.location?.href)) || null
  return url?.searchParams.get('gaa_sig') !== null
}

const removeGnsQueryParams = (currentUrl: string) => {
  if (!hasGAAParam()) return currentUrl

  const url = new URL(currentUrl)

  if (hasGAAParam()) {
    const params = url.searchParams

    // Delete all params that start with "gaa_"
    Array.from(params.keys()).forEach((key) => {
      if (key.startsWith('gaa_')) {
        params.delete(key)
      }
    })

    // Mutate the original URL object directly
    url.search = params.toString()
  }

  // Remove swg.debug=1 from hash
  if (url.hash.includes('swg.debug=1')) {
    const hashParams = new URLSearchParams(url.hash.slice(1))
    hashParams.delete('swg.debug')
    const newHash = hashParams.toString()
    url.hash = newHash ? `#${newHash}` : ''
  }

  return url.toString()
}

const initGoogleNewsShowcase = async ({
  userState,
  authorizationUrl,
  queryClient,
}: InitializeGnsParams) => {
  // We need to set the login cookie for the redirectUrl from Onelog
  setLoginCookie()

  const origin = window.location.hostname

  return window?.GaaMetering?.init({
    authorizationUrl,
    userState: userState,
    allowedReferrers: [origin],
    handleLoginPromise: handleLoginPromise(authorizationUrl, userState),
    registerUserPromise: handleRegisterUserPromise(userState),
    publisherEntitlementPromise: publisherEntitlementPromise(userState),
    unlockArticle: () => handleUnlockArticle(queryClient),
    showPaywall: () => handleShowPaywall(userState),
    shouldInitializeSwG: false,
  })
}

export {
  initGoogleNewsShowcase,
  handleSimulateShowcase,
  hasGAAParam,
  removeGnsQueryParams,
}
